import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, MessageCircle, Search, Users, ClipboardCheck, CalendarCheck, Handshake, LifeBuoy, Cog, Factory, DollarSign, Megaphone, UserCog, Cpu, Pill, ChevronRight } from "lucide-react";

const SUPPORT = [
  { icon: Search, t: "Requirement Understanding", d: "We invest time upfront to understand the role, culture-fit, budget, and success metrics." },
  { icon: Users, t: "Candidate Sourcing", d: "Requirement-focused sourcing across our 5000+ pre-screened talent pool and active industry network." },
  { icon: ClipboardCheck, t: "Screening & Shortlisting", d: "Skills verification, reference checks, and behavioural evaluation before any profile reaches your desk." },
  { icon: CalendarCheck, t: "Interview Coordination", d: "We handle scheduling, reminders, feedback capture, and follow-ups across all interview rounds." },
  { icon: Handshake, t: "Offer & Joining Support", d: "Negotiation, offer roll-out, notice period tracking, and pre-joining engagement to reduce drop-offs." },
  { icon: LifeBuoy, t: "Post-Placement Support", d: "Regular check-ins during the probation period plus a 90-day replacement guarantee." },
];

const STEPS = ["Requirement", "Sourcing", "Screening", "Shortlisting", "Interview", "Selection", "Joining"];

const WHY = [
  { t: "Relevant Candidates", d: "Every profile shared matches your JD — no volume-based CV dumps." },
  { t: "Requirement-Focused Sourcing", d: "Dedicated recruiter aligned to your role, not a call-centre process." },
  { t: "Faster Turnaround", d: "First shortlist typically within 48–72 hours for most mid-senior roles." },
  { t: "Industry Expertise", d: "Domain-specialised recruiters across engineering, pharma, finance, IT and more." },
  { t: "Dedicated Coordination", d: "Single point of contact from requirement to joining, no confusion." },
  { t: "Long-Term Support", d: "Replacement guarantee plus continued relationship for future hiring." },
];

const AREAS = [
  { icon: Cog, t: "Engineering & Manufacturing" },
  { icon: DollarSign, t: "Finance & Accounts" },
  { icon: Megaphone, t: "Sales & Marketing" },
  { icon: UserCog, t: "HR & Administration" },
  { icon: Cpu, t: "IT & Technology" },
  { icon: Pill, t: "Pharma & Healthcare" },
];

export default function ForEmployers() {
  const [f, setF] = useState({
    company_name: "", contact_name: "", email: "", phone: "",
    job_title: "", vacancies: "", location: "", qualification: "",
    experience: "", salary_ctc: "", key_skills: "", details: "",
    industry: "Engineering & Manufacturing", urgency: "Standard (2 weeks)",
  });
  const [jd, setJd] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v || ""));
      // positions field is required by backend; derive from job_title + vacancies
      fd.set("positions", `${f.job_title || "Multiple roles"}${f.vacancies ? ` × ${f.vacancies}` : ""}`);
      if (jd) fd.append("jd_file", jd);
      await api.post("/hiring-request", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSubmitted(true);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="for-employers-page">
      {/* HERO */}
      <section className="bg-[#0F2A4A] text-white py-20">
        <div className="ae-container">
          <div className="max-w-3xl">
            <div className="ae-overline mb-4">For Employers</div>
            <h1 className="text-4xl md:text-6xl font-light leading-tight">
              Hire the Right Talent<br/>
              <span className="font-serif italic text-[#C5A059]">With Confidence.</span>
            </h1>
            <p className="mt-6 text-white/80 text-lg leading-relaxed max-w-2xl">
              Achievers Enterprise handles end-to-end recruitment for you — sourcing, screening,
              interview coordination and joining support — so your team can stay focused on business.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#requirement-form" className="ae-btn-primary text-sm flex items-center gap-2" data-testid="cta-primary">
                Submit Your Hiring Requirement <ArrowRight className="w-4 h-4" />
              </a>
              <a href="https://wa.me/919099042472?text=Hi%20Achievers%20Enterprise%2C%20I%20have%20a%20hiring%20requirement%20I%27d%20like%20to%20discuss."
                 target="_blank" rel="noreferrer" data-testid="cta-wa"
                 className="border-2 border-white text-white hover:bg-white hover:text-[#0F2A4A] rounded-xl px-6 py-3 font-medium transition-all text-sm inline-flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Our Recruitment Support */}
      <section className="py-20 bg-white">
        <div className="ae-container">
          <div className="text-center mb-14">
            <div className="ae-overline mb-3">Our Recruitment Support</div>
            <h2 className="text-3xl md:text-4xl font-medium text-[#0F2A4A]">
              End-to-end hiring, <span className="font-serif italic text-[#C5A059]">handled for you</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SUPPORT.map((s, i) => (
              <div key={i} className="ae-card p-7" data-testid={`support-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-[#C5A059]/10 flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5 text-[#C5A059]" />
                </div>
                <h3 className="font-heading font-medium text-lg text-[#0F2A4A] mb-2">{s.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="ae-container">
          <div className="text-center mb-14">
            <div className="ae-overline mb-3">How It Works</div>
            <h2 className="text-3xl md:text-4xl font-medium text-[#0F2A4A]">
              A simple <span className="font-serif italic text-[#C5A059]">7-step process</span>
            </h2>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className="ae-card px-5 py-3 flex items-center gap-2" data-testid={`step-${i}`}>
                  <span className="font-serif text-[#C5A059] text-lg">0{i + 1}</span>
                  <span className="text-sm font-medium text-[#0F2A4A]">{s}</span>
                </div>
                {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 hidden md:block" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Why Achievers */}
      <section className="py-20 bg-white">
        <div className="ae-container">
          <div className="text-center mb-14">
            <div className="ae-overline mb-3">Why Achievers Enterprise</div>
            <h2 className="text-3xl md:text-4xl font-medium text-[#0F2A4A]">
              Recruitment done <span className="font-serif italic text-[#C5A059]">the right way</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHY.map((w, i) => (
              <div key={i} className="ae-card p-6" data-testid={`why-${i}`}>
                <CheckCircle2 className="w-5 h-5 text-[#C5A059] mb-3" />
                <h3 className="font-heading font-medium text-[#0F2A4A] mb-2">{w.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Recruitment Areas */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="ae-container">
          <div className="text-center mb-14">
            <div className="ae-overline mb-3">Key Recruitment Areas</div>
            <h2 className="text-3xl md:text-4xl font-medium text-[#0F2A4A]">Domain <span className="font-serif italic text-[#C5A059]">specialisation</span></h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {AREAS.map((a, i) => (
              <div key={i} className="ae-card p-6 text-center group" data-testid={`area-${i}`}>
                <div className="w-14 h-14 mx-auto rounded-xl bg-[#0F2A4A] group-hover:bg-[#C5A059] flex items-center justify-center mb-3 transition-colors">
                  <a.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-sm font-medium text-[#0F2A4A]">{a.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="requirement-form" className="py-20 bg-white">
        <div className="ae-container max-w-3xl">
          <div className="text-center mb-10">
            <div className="ae-overline mb-3">Submit Your Hiring Requirement</div>
            <h2 className="text-3xl md:text-4xl font-medium text-[#0F2A4A]">Tell us <span className="font-serif italic text-[#C5A059]">who you need</span></h2>
          </div>

          {submitted ? (
            <div className="ae-card p-12 text-center" data-testid="hire-success">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="font-heading text-2xl font-medium text-[#0F2A4A] mb-3">Thank you for sharing your hiring requirement.</h3>
              <p className="text-slate-600 max-w-xl mx-auto">Our recruitment team will review the details and contact you shortly.</p>
              <a href="https://wa.me/919099042472" target="_blank" rel="noreferrer" className="mt-6 inline-flex ae-btn-primary text-sm gap-2">
                <MessageCircle className="w-4 h-4" /> Message us on WhatsApp
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="ae-card p-8 space-y-4" data-testid="hiring-form">
              <div className="grid md:grid-cols-2 gap-4">
                <FieldIn label="Company Name *" val={f.company_name} onChange={v => setF({ ...f, company_name: v })} tid="hr-company" required />
                <FieldIn label="Contact Person *" val={f.contact_name} onChange={v => setF({ ...f, contact_name: v })} tid="hr-name" required />
                <FieldIn label="Mobile *" val={f.phone} onChange={v => setF({ ...f, phone: v })} tid="hr-phone" required />
                <FieldIn label="Business Email *" val={f.email} onChange={v => setF({ ...f, email: v })} type="email" tid="hr-email" required />
                <FieldIn label="Job Title *" val={f.job_title} onChange={v => setF({ ...f, job_title: v })} tid="hr-title" required />
                <FieldIn label="Vacancies" val={f.vacancies} onChange={v => setF({ ...f, vacancies: v })} tid="hr-vac" placeholder="e.g., 3" />
                <FieldIn label="Location" val={f.location} onChange={v => setF({ ...f, location: v })} tid="hr-loc" />
                <FieldIn label="Qualification" val={f.qualification} onChange={v => setF({ ...f, qualification: v })} tid="hr-qual" />
                <FieldIn label="Experience" val={f.experience} onChange={v => setF({ ...f, experience: v })} tid="hr-exp" placeholder="e.g., 3–5 years" />
                <FieldIn label="Salary / CTC" val={f.salary_ctc} onChange={v => setF({ ...f, salary_ctc: v })} tid="hr-ctc" placeholder="e.g., ₹6–10 LPA" />
                <FieldSel label="Industry" val={f.industry} onChange={v => setF({ ...f, industry: v })} tid="hr-industry"
                  opts={AREAS.map(a => a.t)} />
                <FieldSel label="Urgency" val={f.urgency} onChange={v => setF({ ...f, urgency: v })} tid="hr-urgency"
                  opts={["Immediate (this week)", "Standard (2 weeks)", "Planned (1 month+)"]} />
              </div>
              <FieldIn label="Key Skills" val={f.key_skills} onChange={v => setF({ ...f, key_skills: v })} tid="hr-skills" placeholder="Comma-separated" />
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Job Description</label>
                <textarea rows={4} value={f.details} onChange={e => setF({ ...f, details: e.target.value })}
                  data-testid="hr-details" placeholder="Responsibilities, ideal candidate profile, other notes…"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] outline-none px-4 py-3 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">JD Upload (optional)</label>
                <label className="ae-btn-outline inline-flex items-center gap-2 cursor-pointer text-sm" data-testid="jd-upload-label">
                  📎 {jd ? jd.name : "Attach JD (PDF / DOC)"}
                  <input type="file" accept=".pdf,.doc,.docx" onChange={e => setJd(e.target.files[0])} className="hidden" data-testid="jd-input" />
                </label>
              </div>
              <button type="submit" disabled={submitting} className="ae-btn-primary w-full" data-testid="hr-submit">
                {submitting ? "Submitting…" : "Submit Requirement"}
              </button>
              <p className="text-xs text-center text-slate-500 pt-2">
                Prefer to chat? <a href="https://wa.me/919099042472" target="_blank" rel="noreferrer" className="text-[#C5A059] font-medium">WhatsApp us on +91 90990 42472</a>
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const FieldIn = ({ label, val, onChange, type = "text", tid, required, placeholder }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">{label}</label>
    <input required={required} type={type} value={val} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      data-testid={tid} className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] outline-none px-4 py-3 text-sm" />
  </div>
);

const FieldSel = ({ label, val, onChange, opts, tid }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">{label}</label>
    <select value={val} onChange={e => onChange(e.target.value)} data-testid={tid}
      className="w-full rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-[#C5A059] px-4 py-3 text-sm">
      {opts.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
